import React from 'react'
import { isMobile } from 'react-device-detect'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { OrdersContext } from 'contexts/OrdersContext'
import moment from 'moment'
import {
  $DCprimaryColor,
  ButtonsGroup,
  DcButton,
  DcDiv,
  DcFlexBox,
  Link,
  PixelCurrency,
  PixelDropDownMenu,
  PixelFactoryContext,
  PixelIcon,
  PixelSpinner,
  PixelText,
  PixelTooltip,
  Section,
  SectionContent,
} from 'pixel-base'
import { of } from 'rxjs'
import { AjaxResponse } from 'rxjs/ajax'
import { env } from 'shared/env'
import { useApi } from 'shared/hooks/useApi'
import DcOverlaySlider from 'shared/pixel-base/components/dc-overlay-slider/dc-overlay-slider'
import styled, { css } from 'styled-components'

import { get, post, put } from 'utils/api-routes.util'

import { groupStatus } from '../components/OrderStatus'
import { MergeOrders } from '../SplitAndMerge/MergeOrders'

import AddDiscount from './components/AddDiscount'
import Communication from './components/Communication'
import Complaint from './components/Complaint'
import Consignment from './components/Consignment'
import CustomerInformation from './components/CustomerInformation'
import CustomerNote from './components/CustomerNote'
import DarazPayments from './components/DarazPayments'
import OrderPayments from './components/OrderPayments'
import OrderProduct from './components/OrderProduct'
import OrderShipping from './components/OrderShipping'
import StoreOrder from './components/StoreOrder'
import Tags from './components/Tags'
import { IAllowedStatus } from '../../../../decl'
import { OrderDetail, IMeta, IFilters } from '../../../../decl.d'

const baseURL = env.SERVER_ADDR

// Define the OrderDetails interface locally for type safety

interface IProps {
  filters: {
    store_id: string
    courier_id: string
    tag: string
    start_date: string
    end_date: string
    id: string
    status_classification_id: string
  }
  orderId: string
  setOrderId: (orderId: string) => void
  isNewOrder: boolean
  setIsNewOrder: (isNewOrder: boolean) => void
  SetShowOrderDetailModal: (show: boolean) => void
  splitOrder: {
    isSplit: boolean
    splitId: string
  }
  setSplitOrder: (splitOrder: { isSplit: boolean; splitId: string }) => void
  setReload: (reload: boolean) => void
}
const OrderDetails = (props: IProps) => {
  const {
    filters,
    orderId,
    setOrderId,
    isNewOrder,
    setIsNewOrder,
    SetShowOrderDetailModal,
    splitOrder,
    setSplitOrder,
    setReload,
  } = props

  const [isShowSpinner, setIsShowSpinner] = React.useState(false)
  const [showMergeOrdersPopup, setShowMergeOrdersPopup] = React.useState(false)
  const [isDuplicate, setIsDuplicate] = React.useState(false)
  const { showSuccess, showFailure } = React.useContext(
    PixelFactoryContext,
  ) as any
  const { state, setStateItem } = React.useContext(OrdersContext)

  const [newOrder, setNewOrder] = React.useState(isNewOrder || false)
  const [navigationProcess, setnavigationProcess] = React.useState({
    nextId: null as string | null,
    prevId: null as string | null,
    total_orders: 0,
    curr_order: 0,
  })
  const [customerNote, setCustomerNote] = React.useState([])
  const [groupedStatus, setGroupedStatus] = React.useState<IAllowedStatus[]>([])
  const [grandTotal, setGrandTotal] = React.useState(0)
  const [newOrderData, setNewOrderData] = React.useState({
    activeTab: null,
    advance_payment: null,
    billing: {
      address_id: null,
      address1: null,
      city: null,
      country: null,
      country_code: 'PK',
      email: null,
      first_name: null,
      last_name: null,
      phone: null,
      postcode: null,
    },
    complaint_count: null,
    courier: null,
    courier_transactions: [],
    created_via: null,
    customer_id: null,
    date_created: null,
    discounts: [],
    editPermissions: null,
    fee_lines: [],
    fulfillments: [],
    history: [],
    inventory: [],
    isDisabled: null,
    isEditing: null,
    is_billing_address_same: true,
    is_editable: null,
    is_refuser: null,
    is_returning: null,
    print_customer_note_on_shipping_label: null,
    print_customer_note_on_invoice: null,
    label_name: null,
    last_modified: null,
    last_user_commented: null,
    line_items: [],
    meta: {
      added_by: null,
      business_name: null,
      last_modified: null,
      modified_by: null,
      order_source: null,
      sale_by: null,
      sale_by_id: null,
    },
    notes: [],
    number: null,
    order_discount: null,
    order_tags: [],
    payment_lines: [],
    shipping: {
      address_id: null,
      address1: null,
      city: null,
      country: null,
      country_code: 'PK',
      email: null,
      first_name: null,
      last_name: null,
      phone: null,
      postcode: null,
    },
    shipping_lines: [],
    source_order_id: null,
    status: 'new',
    store_id: null,
    store_name: null,
    store_order_id: null,
    tags: null,
    total: null,
    tracking_id: null,
    transactions: [],
    daraz_payment_transactions: [],
  })

  const [orderDetailsReloadKey, setOrderDetailsReloadKey] = React.useState(0)

  React.useEffect(() => {
    if (groupedStatus.length === 0) setGroupedStatus(state.groupedStatuses)
  }, [state.groupedStatuses])

  React.useEffect(() => {
    if (isNewOrder && orderId == null) {
      setNewOrder(true)
    }
    if (!isNewOrder && orderId != null) {
      refetchOrderDetails()
      setNewOrder(false)
    }
  }, [isNewOrder, orderId])

  React.useEffect(() => {
    const statuses = groupStatus(state, setStateItem)
    setGroupedStatus(statuses)
  }, [state?.allowedStatuses])

  const [details, setDetails] = React.useState<OrderDetail | null>(null)

  const formatFilters = {
    store_id: filters?.store_id || null,
    courier_id: filters?.courier_id || '',
    tag: filters?.tag || '',
    start_date:
      filters?.start_date === null
        ? moment().subtract(30, 'days').format('YYYY-MM-DD')
        : moment(filters?.start_date).format('YYYY-MM-DD'),
    end_date:
      filters?.end_date === null
        ? moment().format('YYYY-MM-DD')
        : moment(filters?.end_date).format('YYYY-MM-DD'),
    id: filters?.id || '',
    status_classification_id: filters?.status_classification_id || null,
  }

  const activeOrderIdRef = React.useRef<string | null>(orderId)
  activeOrderIdRef.current = orderId

  useApi<{ data: OrderDetail; _meta: IMeta }>(
    () =>
      orderId
        ? get(`/orders/${orderId}`, formatFilters as IFilters)
        : of({ data: null, _meta: {} as IMeta }),
    {
      deps: [orderId, JSON.stringify(formatFilters), orderDetailsReloadKey],
      skip: !orderId,
      onSuccess: (res: { data: OrderDetail; _meta: IMeta }) => {
        if (activeOrderIdRef.current !== orderId) return
        if (res?.data) {
          setDetails(res.data)
          setIsDuplicate(res.data.is_duplicate)
          setnavigationProcess({
            nextId: res._meta?.next || null,
            prevId: res._meta?.previous || null,
            total_orders: parseInt(res._meta?.total_orders || '0', 10),
            curr_order: parseInt(res._meta?.current || '0', 10),
          })
          setIsShowSpinner(false)
          if (res.data) {
            const notes = []
            res.data.order_notes?.map((note) => {
              if ((note as any).comment_type_id == 2) {
                notes.push(note)
              }
            })
            setCustomerNote(notes)
          }
        } else {
          showFailure('Order not found')
          SetShowOrderDetailModal(false)
        }
      },
      onError: () => {
        if (activeOrderIdRef.current !== orderId) return
        showFailure('Order not found')
        SetShowOrderDetailModal(false)
      },
    },
  )

  const { token } = JSON.parse(
    localStorage.getItem('user_info') || '{token: null}',
  )

  React.useEffect(() => {
    const GrandTotal =
      newOrderData?.shipping_lines?.reduce((acc, cur) => {
        return acc + Number(cur.total)
      }, 0) +
      newOrderData?.line_items?.reduce((acc, cur) => {
        return acc + Number(cur.price) * Number(cur.quantity)
      }, 0) +
      newOrderData?.payment_lines?.reduce((acc, cur) => {
        return acc + Number(cur.amount)
      }, 0) -
      newOrderData?.discounts?.reduce((acc, cur) => {
        return acc + Number(cur.amount)
      }, 0)
    setGrandTotal(GrandTotal)
  }, [newOrderData])

  const handleAddNewOrder = () => {
    setIsShowSpinner(true)
    const orderData = {
      ...newOrderData,
    }
    Object.keys(orderData.billing).forEach((key) => {
      orderData.billing[key] = orderData.billing[key] || null
    })
    Object.keys(orderData.shipping).forEach((key) => {
      orderData.shipping[key] = orderData.shipping[key] || null
    })
    orderData.line_itms = newOrderData.line_items.map((item) => {
      item.total = newOrderData?.line_items?.reduce((acc, cur) => {
        return acc + Number(cur.price) * Number(cur.quantity)
      }, 0)
      return item
    })
    orderData.total = grandTotal
    post('/orders', orderData).subscribe((res: AjaxResponse<any>) => {
      setIsShowSpinner(false)
      if (res.response.data) {
        showSuccess('Order added successfully')
        setIsNewOrder(false)
        setOrderId(res.response?.data?.number)
      } else {
        if (res.response.error) {
          showFailure(res.response.field + 'is missing')
        }
      }
    })
  }
  const [newStatus, setNewStatus] = React.useState<any>({
    status: '',
  })
  const updateOrderStatus = () => {
    put(`/orders/${orderId}/status`, newStatus).subscribe(
      (res: AjaxResponse<any>) => {
        if (res.response.data) {
          showSuccess('Status changed successfully')
          groupStatus(state, setStateItem)
          setDetails(res.response.data)
        }
      },
    )
  }
  React.useEffect(() => {
    if (newStatus.status !== '') {
      updateOrderStatus()
    }
  }, [newStatus])
  const SplitDisable = () => {
    if (details?.is_editable === false) {
      return true
    }
    if (details?.is_split_child) {
      return true
    }
    if (details?.line_items?.length > 1) {
      return false
    }
    if (
      details?.line_items?.length === 1 &&
      details?.line_items[0].quantity > 1
    ) {
      return false
    }
    return true
  }
  const TransactionOptions = [
    {
      label: 'Split Order',
      disabled: SplitDisable(),
      clickHandler: () => {
        setSplitOrder({ ...splitOrder, isSplit: true, splitId: orderId })
      },
    },
    {
      label: 'Merge Order',
      disabled: !isDuplicate,
      clickHandler: () => {
        setShowMergeOrdersPopup(true)
      },
    },
    {
      label: 'Print Invoice',
      disabled: false,
      clickHandler: () => {
        window.open(`${baseURL}api/v2/print/orders/${token}/${orderId}`)
      },
    },
  ]
  const getIdsFromString = (str) => {
    const ids = str.split(',')
    return ids
  }
  React.useEffect(() => {
    !details && setIsShowSpinner(true)
  }, [details])
  React.useEffect(() => {
    if (orderId == null) {
      setDetails(null)
      setCustomerNote([])
    }
    if (newOrder) {
      setIsShowSpinner(false)
    }
  }, [orderId, newOrder])

  const refetchOrderDetails = () => setOrderDetailsReloadKey((k) => k + 1)

  return (
    <>
      <PixelSpinner isShow={isShowSpinner} />
      <form style={{ height: 'calc(100vh - 110px)' }}>
        <OrdersContainer>
          <DetailContainer>
            {!newOrder && (
              <DcFlexBox
                justifyContent="space-between"
                alignItems="flex-end"
                padding="0.5rem 0.5rem"
                width=" 100%"
                position="sticky"
              >
                <DcDiv width="70%">
                  <DcFlexBox
                    DcFlexBox
                    flexDirection="row"
                    alignItems="center"
                    fontSize="16px"
                    fontWeight="600"
                    color=" #202223"
                  >
                    {details && (
                      <DcFlexBox
                        DcFlexBox
                        flexDirection="row"
                        alignItems="center"
                        fontSize="16px"
                        fontWeight="600"
                        color=" #202223"
                        width="auto"
                      >
                        Ref #{' '}
                        {details?.is_split_child
                          ? details?.split_source_order_id
                          : details?.source_order_id}
                      </DcFlexBox>
                    )}

                    {details?.is_split_main ? (
                      <DcDiv style={{ fontSize: '12px', fontWeight: '400' }}>
                        This order was split into the following orders:{' '}
                        <Link
                          onClick={() =>
                            setOrderId(
                              getIdsFromString(details.split_child_ids)[0],
                            )
                          }
                        >
                          {getIdsFromString(details.split_source_order_id)[0]}
                        </Link>
                        ,
                        <Link
                          onClick={() =>
                            setOrderId(
                              getIdsFromString(details.split_child_ids)[1],
                            )
                          }
                        >
                          {getIdsFromString(details?.split_source_order_id)[1]}
                        </Link>
                      </DcDiv>
                    ) : (
                      <>
                        {details?.is_split_child && (
                          <DcDiv
                            style={{ fontSize: '12px', fontWeight: '400' }}
                          >
                            This order was split from{' '}
                            <Link
                              onClick={() => setOrderId(details?.split_main_id)}
                            >
                              {details?.split_main_id}
                            </Link>
                          </DcDiv>
                        )}

                        {details?.exchanged_from_order_id && (
                          <DcDiv
                            style={{ fontSize: '12px', fontWeight: '400' }}
                          >
                            &nbsp; This order has been created as an exchange
                            for Order Id&nbsp;
                            <Link
                              style={{
                                fontSize: '12px',
                                color: $DCprimaryColor,
                              }}
                              onClick={() =>
                                setOrderId(details?.exchanged_from_order_id)
                              }
                            >
                              {details?.exchanged_from_order_id}
                            </Link>
                          </DcDiv>
                        )}
                      </>
                    )}
                  </DcFlexBox>
                </DcDiv>
                <DcFlexBox
                  DcFlexBox
                  justifyContent=" flex-end"
                  flexDirection="row"
                  alignItems="flex-end"
                  gap="10px"
                >
                  <PixelDropDownMenu
                    disabled={!details?.is_editable}
                    size="sm"
                    variant="tag"
                    color={$DCprimaryColor}
                    isGrouped
                    options={groupedStatus.map((status) => {
                      return {
                        ...status,
                        label: status.name,
                        formatter: (option, i) => {
                          return (
                            <DropdownLine key={i}>{option.label}</DropdownLine>
                          )
                        },
                        children: status.children.map((child) => {
                          return {
                            ...child,
                            label: child.title,
                            disabled: false,
                            formatter: (option) => {
                              return (
                                <>
                                  {option ? (
                                    <DropdownLine>
                                      <LeftSide>{option.label}</LeftSide>
                                    </DropdownLine>
                                  ) : (
                                    'Not available'
                                  )}
                                </>
                              )
                            },
                            clickHandler: () => {
                              setNewStatus({
                                status: child.id,
                              })
                            },
                          }
                        }),
                      }
                    })}
                    toggleText={`${details?.status}`}
                  />
                  <PixelDropDownMenu
                    variant={'outline'}
                    size="sm"
                    style={{ color: $DCprimaryColor }}
                    color={$DCprimaryColor}
                    options={TransactionOptions}
                    toggleText={'More Actions'}
                  />
                  <ButtonsGroup
                    style={{
                      gap: '15px',
                      alignItems: 'center',
                      flexDirection: 'row',
                    }}
                  >
                    <PixelTooltip tooltipText="Previous">
                      <DcButton
                        disabled={navigationProcess.prevId === null}
                        onClick={() => {
                          // navigate(`/orders/detail/${navigationProcess.prevId}`)
                          setOrderId(navigationProcess.prevId)
                        }}
                        size="sm"
                        variant="outline-primary"
                      >
                        <PixelIcon color="inherit" icon={faChevronLeft} />
                      </DcButton>
                    </PixelTooltip>
                    <DcDiv
                      style={{
                        fontSize: '12px',
                        fontWeight: '400',
                        color: 'rgba(0, 0, 0, 0.84)',
                      }}
                    >
                      {navigationProcess.curr_order}/
                      {navigationProcess.total_orders}
                    </DcDiv>
                    <PixelTooltip tooltipText="Next">
                      <DcButton
                        disabled={navigationProcess.nextId === null}
                        onClick={() => {
                          // navigate(`/orders/detail/${navigationProcess.nextId}`)
                          setOrderId(navigationProcess.nextId)
                        }}
                        size="sm"
                        variant="outline-primary"
                      >
                        <PixelIcon color="inherit" icon={faChevronRight} />
                      </DcButton>
                    </PixelTooltip>
                  </ButtonsGroup>
                </DcFlexBox>
              </DcFlexBox>
            )}

            <DetailContainerContent isMobile={isMobile}>
              <DcFlexBox
                flexDirection="column"
                justifyContent="flex-start"
                width={isMobile ? '100%' : '64%'}
                gap="18px"
              >
                <Section noPadding>
                  <SectionContent noPadding>
                    <OrderProduct
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      ComplaintSection={false}
                      newOrder={newOrder}
                      setNewOrderData={setNewOrderData}
                      newOrderData={newOrderData}
                    />
                  </SectionContent>
                </Section>
                <Section noPadding>
                  <SectionContent noPadding>
                    <AddDiscount
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      newOrder={newOrder}
                      newOrderData={newOrderData}
                      setNewOrderData={setNewOrderData}
                    />
                  </SectionContent>
                </Section>
                <Section noPadding>
                  <SectionContent noPadding>
                    <OrderPayments
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      newOrder={newOrder}
                      newOrderData={newOrderData}
                      setNewOrderData={setNewOrderData}
                    />
                  </SectionContent>
                </Section>
                {details?.daraz_payment_transactions?.length > 0 && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <DarazPayments details={details} />
                    </SectionContent>
                  </Section>
                )}

                <Section noPadding>
                  <SectionContent noPadding>
                    <OrderShipping
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      newOrder={newOrder}
                      newOrderData={newOrderData}
                      setNewOrderData={setNewOrderData}
                    />
                  </SectionContent>
                </Section>
                {newOrder && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <InnerSection>
                        <PaymentContainer>
                          <PixelText
                            textSize="16px"
                            color="rgba(0, 0, 0, 0.84)"
                            style={{ padding: '0px 10px', fontWeight: '600' }}
                          >
                            Total
                          </PixelText>
                          <Total>
                            <PixelCurrency value={grandTotal} />
                          </Total>
                        </PaymentContainer>
                      </InnerSection>
                      <FormGroup>
                        <DcButton
                          onClick={() => {
                            SetShowOrderDetailModal(false)
                          }}
                          // onClick={() => {
                          //   navigate('/orders')
                          // }}
                          variant="outline-secondary"
                          size="sm"
                        >
                          Cancel
                        </DcButton>
                        <DcButton
                          disabled={
                            newOrderData?.line_items?.length === 0 ||
                            newOrderData?.shipping?.first_name === null ||
                            newOrderData?.billing?.first_name === null
                          }
                          onClick={handleAddNewOrder}
                          size="sm"
                        >
                          Create Order
                        </DcButton>
                      </FormGroup>
                    </SectionContent>
                  </Section>
                )}

                {!newOrder && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <Communication
                        detailID={orderId}
                        setDetails={setDetails}
                        details={details}
                      />
                    </SectionContent>
                  </Section>
                )}
              </DcFlexBox>
              <DcFlexBox
                flexDirection="column"
                justifyContent="flex-start"
                width={isMobile ? '100%' : '35%'}
                gap="18px"
              >
                {customerNote?.length > 0 && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <CustomerNote
                        detailID={orderId}
                        customerNote={customerNote}
                        details={details}
                        setDetails={setDetails}
                      />
                    </SectionContent>
                  </Section>
                )}
                <Section noPadding>
                  <SectionContent noPadding>
                    <CustomerInformation
                      setNewOrderData={setNewOrderData}
                      newOrderData={newOrderData}
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      newOrder={newOrder}
                      isItInvoice={false}
                    />
                  </SectionContent>
                </Section>
                {!newOrder && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <Consignment
                        getOrderDetails={refetchOrderDetails}
                        detailID={orderId}
                        setDetails={setDetails}
                        order={details}
                        setReload={setReload}
                      />
                    </SectionContent>
                  </Section>
                )}
                <Section noPadding>
                  <SectionContent noPadding>
                    <StoreOrder
                      detailID={orderId}
                      setDetails={setDetails}
                      details={details}
                      newOrder={newOrder}
                      newOrderData={newOrderData}
                      setNewOrderData={setNewOrderData}
                    />
                  </SectionContent>
                </Section>
                {!newOrder && details?.status !== '' && (
                  <Section noPadding>
                    <SectionContent
                      style={{ flexDirection: 'column' }}
                      noPadding
                    >
                      <Complaint
                        detailID={orderId}
                        setDetails={setDetails}
                        order={details}
                      />
                    </SectionContent>
                  </Section>
                )}
                {!newOrder && (
                  <Section noPadding>
                    <SectionContent noPadding>
                      <Tags
                        detailID={orderId}
                        setDetails={setDetails}
                        details={details}
                      />
                    </SectionContent>
                  </Section>
                )}
              </DcFlexBox>
            </DetailContainerContent>
          </DetailContainer>
        </OrdersContainer>
      </form>

      <DcOverlaySlider
        width={'40%'}
        title={'Merge Duplicate Orders'}
        onClose={() => setShowMergeOrdersPopup(false)}
        isOpen={showMergeOrdersPopup}
      >
        {showMergeOrdersPopup && (
          <MergeOrders
            orderID={orderId}
            details={details}
            handleClose={(data?) => {
              if (data === 'success') {
                setTimeout(() => {
                  refetchOrderDetails()
                }, 500)
              }
              setShowMergeOrdersPopup(false)
            }}
          />
        )}
      </DcOverlaySlider>
    </>
  )
}

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 65%;
  gap: 18px;
`
const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 35%;
  gap: 18px;
`
const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #fff;
  width: 100%;
  transition: all 0.3s ease-in-out;
  @media (max-width: 1400px) {
    width: 100%;
  }
  @media (max-width: 1200px) {
    width: 98%;
  }
  margin: 0 auto;
`
const DetailContainerContent = styled.div<{ isMobile?: boolean }>`
  display: flex;
  max-height: 85.8vh;
  height: 100%;
  overflow: auto;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  width: 100%;
  padding-top: 10px;
  padding: 20px 0px 12px 12px;
  background-color: #edf2f9;
  ${(props) =>
    props.isMobile &&
    css`
      ${RightSection}, ${LeftSection} {
        width: 100%;
      }
    `}
`

const OrdersContainer = styled.div`
  width: 100%;
  height: auto;
  background-color: #f8f8f9;
  padding-bottom: 10px;
`

const FormGroup = styled.div`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: stretch;
  align-content: flex-start;
  width: 100%;
  gap: 10px;
  padding: 10px;
`
const PaymentContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  width: 100%;
`
const Total = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.84);
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 50%;
  padding-right: 25px;
`
const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-content: center;
  border-bottom: 1px solid #e6e6e6;
  width: 100%;
  padding: 10px 0px;
`
const DropdownLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  width: 100%;
`
const LeftSide = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.84);
`

export default OrderDetails
